import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const TeamMemberTable = ({ members, onUpdateRole, onUpdatePermissions, onRemoveMember, onBulkAction }) => {
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showLegend, setShowLegend] = useState(false);

  const roleColors = {
    'Owner': 'bg-accent/10 text-accent',
    'Designer': 'bg-blue-50 text-blue-700',
    'Sales Rep': 'bg-green-50 text-green-700',
    'Manager': 'bg-purple-50 text-purple-700'
  };

  const statusColors = {
    'Active': 'bg-success/10 text-success',
    'Pending': 'bg-warning/10 text-warning',
    'Inactive': 'bg-error/10 text-error'
  };

  // Permission legend data
  const permissionLegend = [
    { key: 'createProjects', label: 'Create Projects', color: 'bg-success', icon: 'Plus' },
    { key: 'viewAllProjects', label: 'View All Projects', color: 'bg-blue-500', icon: 'Eye' },
    { key: 'manageTeam', label: 'Manage Team', color: 'bg-purple-500', icon: 'Users' },
    { key: 'accessVault', label: 'Access Vault', color: 'bg-accent', icon: 'Archive' },
    { key: 'clientCommunication', label: 'Client Communication', color: 'bg-orange-500', icon: 'MessageCircle' }
  ];

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedMembers(members?.filter(m => m?.role !== 'Owner')?.map(m => m?.id));
    } else {
      setSelectedMembers([]);
    }
  };

  const handleSelectMember = (memberId, checked) => {
    if (checked) {
      setSelectedMembers([...selectedMembers, memberId]);
    } else {
      setSelectedMembers(selectedMembers?.filter(id => id !== memberId));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedMembers = [...members]?.sort((a, b) => {
    const aValue = a?.[sortField];
    const bValue = b?.[sortField];
    const direction = sortDirection === 'asc' ? 1 : -1;
    
    if (typeof aValue === 'string') {
      return aValue?.localeCompare(bValue) * direction;
    }
    return (aValue - bValue) * direction;
  });

  const handleBulkAction = (action) => {
    onBulkAction(action, selectedMembers);
    setSelectedMembers([]);
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden elevation-1">
      {/* Permission Legend */}
      <div className="bg-muted/30 border-b border-border p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-foreground">Permissions Legend:</span>
            <div className="flex items-center space-x-3">
              {permissionLegend?.map((permission) => (
                <div key={permission?.key} className="flex items-center space-x-1">
                  <div className={`w-2 h-2 ${permission?.color} rounded-full`} />
                  <span className="text-xs text-muted-foreground">{permission?.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Bulk Actions */}
      {selectedMembers?.length > 0 && (
        <div className="bg-accent/5 border-b border-border p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              {selectedMembers?.length} member{selectedMembers?.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('activate')}
              >
                Activate
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('deactivate')}
              >
                Deactivate
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleBulkAction('remove')}
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left p-4 w-12">
                <Checkbox
                  checked={selectedMembers?.length === members?.filter(m => m?.role !== 'Owner')?.length}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-accent transition-luxury"
                >
                  <span>Member</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('role')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-accent transition-luxury"
                >
                  <span>Role</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-accent transition-luxury"
                >
                  <span>Status</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4">Permissions</th>
              <th className="text-left p-4">Last Active</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedMembers?.map((member) => (
              <tr key={member?.id} className="border-b border-border hover:bg-muted/30 transition-luxury">
                <td className="p-4">
                  <Checkbox
                    checked={selectedMembers?.includes(member?.id)}
                    onChange={(e) => handleSelectMember(member?.id, e?.target?.checked)}
                    disabled={member?.role === 'Owner'}
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                      <Icon name="User" size={16} color="var(--color-secondary-foreground)" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{member?.name}</p>
                      <p className="text-sm text-muted-foreground">{member?.email}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors?.[member?.role] || 'bg-muted text-muted-foreground'}`}>
                    {member?.role}
                  </span>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors?.[member?.status] || 'bg-muted text-muted-foreground'}`}>
                    {member?.status}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-1">
                    {member?.permissions?.createProjects && (
                      <div 
                        className="w-2 h-2 bg-success rounded-full cursor-help" 
                        title="Create Projects - Can create new jewelry design projects"
                      />
                    )}
                    {member?.permissions?.viewAllProjects && (
                      <div 
                        className="w-2 h-2 bg-blue-500 rounded-full cursor-help" 
                        title="View All Projects - Can view all team projects"
                      />
                    )}
                    {member?.permissions?.manageTeam && (
                      <div 
                        className="w-2 h-2 bg-purple-500 rounded-full cursor-help" 
                        title="Manage Team - Can invite, edit, and remove team members"
                      />
                    )}
                    {member?.permissions?.accessVault && (
                      <div 
                        className="w-2 h-2 bg-accent rounded-full cursor-help" 
                        title="Access Vault - Can access digital asset vault"
                      />
                    )}
                    {member?.permissions?.clientCommunication && (
                      <div 
                        className="w-2 h-2 bg-orange-500 rounded-full cursor-help" 
                        title="Client Communication - Can communicate directly with clients"
                      />
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">{member?.lastActive}</span>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onUpdateRole(member?.id)}
                      disabled={member?.role === 'Owner'}
                    >
                      <Icon name="Edit" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onRemoveMember(member?.id)}
                      disabled={member?.role === 'Owner'}
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TeamMemberTable;